/**
 * ワークスペースの状態。配列の取込・切替・表示範囲・選択・表示設定をここに集める。
 * 配列本体は `ChunkedSequence`（IndexedDB 遅延読み）で持ち、React の状態には載せない。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChunkedSequence,
  FeatureIndex,
  fullRange,
  type GenomicFeatureV1,
  type Interval,
  type ViewRange,
} from '@kensuke/genome-core';
import {
  IndexedDbChunkSource,
  getFeatures,
  listSequenceMeta,
  putFeatures,
  putSequenceMeta,
  type StoredSequenceMeta,
} from '../../../services/geneAnalysisChunkStore.service';
import {
  importSequenceFile,
  type GeneAnalysisImportProgressMessage,
} from '../../../utils/geneAnalysisImportClient';
import {
  downloadBlob,
  exportSequenceText,
  fileNameFor,
  openGprojFile,
  saveGprojFile,
} from '../../../utils/geneAnalysisProjectIo';
import type { Topology } from '@kensuke/genome-core';

export const GENE_ANALYSIS_PROJECT_ID = 'local';
const DISPLAY_OPTIONS_KEY = 'geneAnalysis.displayOptions.v1';

export type StrandMode = 'single' | 'double';

export interface GeneAnalysisDisplayOptions {
  /** 塩基のフォントサイズ（CSS px） */
  fontSize: number;
  strandMode: StrandMode;
  theme: 'light' | 'dark';
  guanine: 'yellow' | 'black';
  contrast: 'spec' | 'aa';
}

export const DEFAULT_DISPLAY_OPTIONS: GeneAnalysisDisplayOptions = {
  fontSize: 15,
  strandMode: 'single',
  theme: 'light',
  guanine: 'yellow',
  contrast: 'spec',
};

export interface ImportState {
  running: boolean;
  bytesRead: number;
  totalBytes: number;
  basesRead: number;
  sequenceName?: string;
  error?: string;
  warnings: string[];
  elapsedMs?: number;
}

const IDLE_IMPORT: ImportState = { running: false, bytesRead: 0, totalBytes: 0, basesRead: 0, warnings: [] };

function loadDisplayOptions(): GeneAnalysisDisplayOptions {
  if (typeof localStorage === 'undefined') return DEFAULT_DISPLAY_OPTIONS;
  try {
    const raw = localStorage.getItem(DISPLAY_OPTIONS_KEY);
    return raw ? { ...DEFAULT_DISPLAY_OPTIONS, ...(JSON.parse(raw) as Partial<GeneAnalysisDisplayOptions>) } : DEFAULT_DISPLAY_OPTIONS;
  } catch {
    return DEFAULT_DISPLAY_OPTIONS;
  }
}

export function useGeneAnalysisWorkspace() {
  const [sequences, setSequences] = useState<StoredSequenceMeta[]>([]);
  const [activeSequenceId, setActiveSequenceId] = useState<string | null>(null);
  const [features, setFeatures] = useState<GenomicFeatureV1[]>([]);
  const [view, setView] = useState<ViewRange>({ start: 0, end: 0 });
  /**
   * 塩基ビューが実際に描いている範囲。折返し表示はペインの高さぶん行を並べるので、
   * ズーム状態の `view` より広くなる。ステータスとマップはこちらを見せる（§0.3.2 / U159・U160）。
   */
  const [renderedWindow, setRenderedWindow] = useState<ViewRange | null>(null);
  const [selection, setSelection] = useState<Interval | null>(null);
  const [importState, setImportState] = useState<ImportState>(IDLE_IMPORT);
  const [displayOptions, setDisplayOptions] = useState<GeneAnalysisDisplayOptions>(loadDisplayOptions);
  const [restoring, setRestoring] = useState(true);

  const sequenceRef = useRef<ChunkedSequence | null>(null);

  const activeMeta = useMemo(
    () => sequences.find((meta) => meta.sequenceId === activeSequenceId) ?? null,
    [sequences, activeSequenceId]
  );

  const sequence = useMemo(() => {
    if (!activeMeta) {
      sequenceRef.current = null;
      return null;
    }
    const source = new IndexedDbChunkSource(
      activeMeta.projectId,
      activeMeta.sequenceId,
      activeMeta.chunkSize,
      activeMeta.chunkCount
    );
    const next = new ChunkedSequence({
      id: activeMeta.sequenceId,
      name: activeMeta.name,
      description: activeMeta.description,
      length: activeMeta.length,
      topology: activeMeta.topology,
      source,
    });
    sequenceRef.current = next;
    return next;
  }, [activeMeta]);

  const featureIndex = useMemo(
    () =>
      activeMeta
        ? new FeatureIndex(features, activeMeta.length, { topology: activeMeta.topology })
        : null,
    [features, activeMeta]
  );

  // 前回の続き（I02 相当）: IndexedDB に残っている配列を復元する
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stored = await listSequenceMeta(GENE_ANALYSIS_PROJECT_ID);
        if (cancelled || stored.length === 0) return;
        setSequences(stored);
        setActiveSequenceId(stored[0].sequenceId);
      } catch {
        // 復元できないときは空状態のままにする
      } finally {
        if (!cancelled) setRestoring(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // 配列を切り替えたら注釈と表示範囲を入れ替える
  useEffect(() => {
    let cancelled = false;
    setRenderedWindow(null);
    if (!activeMeta) {
      setFeatures([]);
      setView({ start: 0, end: 0 });
      setSelection(null);
      return () => {
        cancelled = true;
      };
    }
    setSelection(null);
    setView(fullRange(activeMeta.length));
    (async () => {
      const loaded = await getFeatures(activeMeta.projectId, activeMeta.sequenceId);
      if (!cancelled) setFeatures(loaded);
    })();
    return () => {
      cancelled = true;
    };
  }, [activeMeta]);

  useEffect(() => {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(DISPLAY_OPTIONS_KEY, JSON.stringify(displayOptions));
  }, [displayOptions]);

  const openProject = useCallback(async (file: File) => {
    setImportState({ ...IDLE_IMPORT, running: true, totalBytes: file.size });
    try {
      await openGprojFile(GENE_ANALYSIS_PROJECT_ID, file);
      const stored = await listSequenceMeta(GENE_ANALYSIS_PROJECT_ID);
      setSequences(stored);
      setActiveSequenceId(stored[0]?.sequenceId ?? null);
      setImportState({ ...IDLE_IMPORT, bytesRead: file.size, totalBytes: file.size });
    } catch (error) {
      setImportState({
        ...IDLE_IMPORT,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, []);

  const importFile = useCallback(async (file: File) => {
    if (/\.gproj$/i.test(file.name)) {
      await openProject(file);
      return;
    }
    setImportState({ ...IDLE_IMPORT, running: true, totalBytes: file.size });
    try {
      const done = await importSequenceFile({
        projectId: GENE_ANALYSIS_PROJECT_ID,
        file,
        onProgress: (progress: GeneAnalysisImportProgressMessage) => {
          setImportState((previous) => ({
            ...previous,
            running: true,
            bytesRead: progress.bytesRead,
            totalBytes: progress.totalBytes,
            basesRead: progress.basesRead,
            sequenceName: progress.sequenceName,
          }));
        },
      });
      const stored = await listSequenceMeta(GENE_ANALYSIS_PROJECT_ID);
      setSequences(stored);
      setActiveSequenceId(stored[0]?.sequenceId ?? null);
      setImportState({
        ...IDLE_IMPORT,
        warnings: done.warnings,
        elapsedMs: done.elapsedMs,
        totalBytes: file.size,
        bytesRead: file.size,
      });
    } catch (error) {
      setImportState({
        ...IDLE_IMPORT,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, [openProject]);

  const reportRenderedWindow = useCallback((next: ViewRange | null) => {
    setRenderedWindow((previous) => {
      if (next === null) return previous === null ? previous : null;
      if (previous && previous.start === next.start && previous.end === next.end) return previous;
      return next;
    });
  }, []);

  const updateDisplayOptions = useCallback((patch: Partial<GeneAnalysisDisplayOptions>) => {
    setDisplayOptions((previous) => ({ ...previous, ...patch }));
  }, []);

  const persistFeatures = useCallback(
    async (next: GenomicFeatureV1[]) => {
      if (!activeMeta) return;
      setFeatures(next);
      await putFeatures(activeMeta.projectId, activeMeta.sequenceId, next);
      await putSequenceMeta({
        projectId: activeMeta.projectId,
        sequenceId: activeMeta.sequenceId,
        name: activeMeta.name,
        description: activeMeta.description,
        length: activeMeta.length,
        topology: activeMeta.topology,
        chunkSize: activeMeta.chunkSize,
        chunkCount: activeMeta.chunkCount,
        featureCount: next.length,
      });
      setSequences((previous) =>
        previous.map((meta) =>
          meta.sequenceId === activeMeta.sequenceId ? { ...meta, featureCount: next.length } : meta
        )
      );
    },
    [activeMeta]
  );

  const addFeature = useCallback(
    (draft: Omit<GenomicFeatureV1, 'id'>) => {
      const id = `feat-${Date.now().toString(36)}`;
      void persistFeatures([...features, { ...draft, id }]);
    },
    [features, persistFeatures]
  );

  const deleteFeature = useCallback(
    (id: string) => {
      void persistFeatures(features.filter((feature) => feature.id !== id));
    },
    [features, persistFeatures]
  );

  const renameActive = useCallback(
    (name: string) => {
      if (!activeMeta) return;
      const next = { ...activeMeta, name };
      void putSequenceMeta({
        projectId: next.projectId,
        sequenceId: next.sequenceId,
        name: next.name,
        description: next.description,
        length: next.length,
        topology: next.topology,
        chunkSize: next.chunkSize,
        chunkCount: next.chunkCount,
        featureCount: next.featureCount,
      });
      setSequences((previous) =>
        previous.map((meta) => (meta.sequenceId === next.sequenceId ? { ...meta, name } : meta))
      );
    },
    [activeMeta]
  );

  const setTopology = useCallback(
    (topology: Topology) => {
      if (!activeMeta) return;
      void putSequenceMeta({
        projectId: activeMeta.projectId,
        sequenceId: activeMeta.sequenceId,
        name: activeMeta.name,
        description: activeMeta.description,
        length: activeMeta.length,
        topology,
        chunkSize: activeMeta.chunkSize,
        chunkCount: activeMeta.chunkCount,
        featureCount: activeMeta.featureCount,
      });
      setSequences((previous) =>
        previous.map((meta) =>
          meta.sequenceId === activeMeta.sequenceId ? { ...meta, topology } : meta
        )
      );
    },
    [activeMeta]
  );

  const saveProject = useCallback(async () => {
    const { blob } = await saveGprojFile(GENE_ANALYSIS_PROJECT_ID, activeMeta?.name ?? 'project');
    downloadBlob(blob, fileNameFor(activeMeta?.name ?? 'project', 'gproj'));
  }, [activeMeta]);

  const exportActive = useCallback(
    async (kind: 'gproj' | 'fasta' | 'genbank') => {
      if (kind === 'gproj') {
        await saveProject();
        return;
      }
      if (!sequence || !activeMeta) return;
      const text = await exportSequenceText(sequence, features, kind);
      downloadBlob(
        new Blob([text], { type: 'text/plain' }),
        fileNameFor(activeMeta.name, kind === 'fasta' ? 'fa' : 'gb')
      );
    },
    [activeMeta, features, saveProject, sequence]
  );

  const jumpTo = useCallback(
    (center: number, span?: number) => {
      if (!activeMeta) return;
      const width = Math.max(20, Math.min(span ?? view.end - view.start, activeMeta.length));
      const start = Math.min(Math.max(0, center - width / 2), Math.max(0, activeMeta.length - width));
      setView({ start, end: start + width });
    },
    [activeMeta, view.end, view.start]
  );

  const selectFeature = useCallback(
    (feature: GenomicFeatureV1) => {
      setSelection({ start: feature.start, end: feature.end });
      jumpTo((feature.start + feature.end) / 2, Math.max(200, (feature.end - feature.start) * 1.6));
    },
    [jumpTo]
  );

  return {
    sequences,
    activeMeta,
    activeSequenceId,
    setActiveSequenceId,
    sequence,
    features,
    featureIndex,
    view,
    setView,
    renderedWindow,
    reportRenderedWindow,
    selection,
    setSelection,
    selectFeature,
    jumpTo,
    importState,
    importFile,
    openProject,
    saveProject,
    exportActive,
    addFeature,
    deleteFeature,
    renameActive,
    setTopology,
    displayOptions,
    updateDisplayOptions,
    restoring,
  };
}

export type GeneAnalysisWorkspaceState = ReturnType<typeof useGeneAnalysisWorkspace>;
